import { ExportFormatEnum } from "@finway-group/shared/lib/models"
import { Col, Collapse, Form, Input, Row, Select } from "antd"
import { FormInstance } from "antd/lib/form"
import { Store } from "antd/lib/form/interface"
import moment from "moment"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import CommonFilterFields from "Components/commonFilterFields"
import { getPopupAnchor } from "Shared/utils"
import { blurInputOnWeel, decodeFilterString, doFilterSelect } from "Shared/utils/helpers"

import { generateRules } from "./rules/export.rules"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { expenseStati } from "Shared/config/consts"
import { getTableFilterQueryStringStore } from "Shared/hooks/table.hooks"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { convertQueryFormatIntoFilterObject } from "Shared/utils/filter.utils"
import { ExpenseFilter } from "./expenseFilter.form"
import { useArchiveInterval } from "Shared/hooks/company.hooks"
import { getFilteredExpenseStatusArrayForExport } from "Features/pages/expenses/expenseUtils"

const { Option } = Select

const adjustFilterObject = (filterObject: ExpenseFilter): any => ({
    ...filterObject,
    minDate: filterObject.minDate ? moment(filterObject.minDate) : "",
    maxDate: filterObject.maxDate ? moment(filterObject.maxDate) : "",
    minInvoiceDate: filterObject.minInvoiceDate ? moment(filterObject.minInvoiceDate) : "",
    maxInvoiceDate: filterObject.maxInvoiceDate ? moment(filterObject.maxInvoiceDate) : "",
})

interface ExportFormInterface {
    subscriptions?: boolean
    transactions?: boolean
    formInstance: FormInstance
    onSubmit: (values: Store) => void
}

const ExportForm: React.FC<ExportFormInterface> = ({ formInstance, subscriptions = false, transactions = false, onSubmit }) => {
    const { t } = useTranslation()
    const dashboardFilterQueryString = getTableFilterQueryStringStore(TablesEnum.DONE_REQUESTS)
    const rules = generateRules(formInstance)
    const archiveAfterXDays = useArchiveInterval()

    // Try to copy the content from expense filter, so the values are prefilled
    const [filterData, onFilterDataChange] = useStateIfMounted(
        adjustFilterObject(
            convertQueryFormatIntoFilterObject(
                decodeFilterString(dashboardFilterQueryString ?? `&datePurchased[gte]=${moment().subtract(1, "M").toISOString()}&datePurchased[lte]=${moment().toISOString()}`)
            )
        )
    )

    const [exportFormat, setExportFormat] = useStateIfMounted(ExportFormatEnum.ALL)

    const anchorRef = useRef(null)

    // Dummy effect re-setting the filterData value. Fixes the wrongly displayed dropdowns
    useEffect(() => {
        onFilterDataChange(
            adjustFilterObject(
                convertQueryFormatIntoFilterObject(
                    decodeFilterString(dashboardFilterQueryString ?? `&datePurchased[gte]=${moment().subtract(1, "M").toISOString()}&datePurchased[lte]=${moment().toISOString()}`)
                )
            )
        )
    }, [])

    // Applying filter data values to the form
    useEffect(() => {
        formInstance.setFieldsValue({ ...filterData })
    }, [filterData])

    // if min date is included in the date range of auto archived expenses then includeArchive is set to true
    const handleValuesChange = (changedFields: any, _allFields: any) => {
        if (changedFields.minDate && moment(changedFields.minDate).isSameOrBefore(moment().subtract(archiveAfterXDays, "d"))) formInstance.setFieldsValue({ includeArchive: true })
    }

    return (
        <Form id="exportForm" form={formInstance} onValuesChange={handleValuesChange} layout="vertical" onFinish={onSubmit}>
            <div ref={anchorRef} />
            <Collapse ghost defaultActiveKey={[0, 1, 2]}>
                <Collapse.Panel header={t("input:filter_form.amount")} key="0">
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <Form.Item label={t("input:filter_form.min_amount")} name="minAmount" rules={rules.minAmount} className="mb-0">
                                <Input type="number" placeholder={t("placeholder:expense_filter.min_amount")} onWheel={blurInputOnWeel} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label={t("input:filter_form.max_amount")} name="maxAmount" rules={rules.maxAmount} className="mb-0">
                                <Input type="number" placeholder={t("placeholder:expense_filter.max_amount")} onWheel={blurInputOnWeel} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Collapse.Panel>
                <Collapse.Panel header={t("input:export.format")} key="1">
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item name="format" className="mb-0" initialValue={ExportFormatEnum.ALL}>
                                <Select
                                    showSearch
                                    filterOption={doFilterSelect}
                                    getPopupContainer={getPopupAnchor(anchorRef.current)}
                                    onSelect={(value) => setExportFormat(value as ExportFormatEnum)}
                                >
                                    <Option value={ExportFormatEnum.ALL} label={t("input:export.export_format.all_formats")}>
                                        {t("input:export.export_format.all_formats")}
                                    </Option>
                                    <Option value={ExportFormatEnum.CSV} label={t("input:export.export_format.csv")}>
                                        {t("input:export.export_format.csv")}
                                    </Option>
                                    <Option value={ExportFormatEnum.DATEV} label={t("input:export.export_format.datev")}>
                                        {t("input:export.export_format.datev")}
                                    </Option>
                                    <Option value={ExportFormatEnum.INVOICES} label={t("input:export.export_format.invoices")}>
                                        {t("input:export.export_format.invoices")}
                                    </Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Collapse.Panel>
                {!subscriptions && !transactions && (
                    <Collapse.Panel header="Status" key="2">
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item name="status" className="mb-0" initialValue="" rules={rules.status} dependencies={["format"]}>
                                    <Select showSearch filterOption={doFilterSelect} getPopupContainer={getPopupAnchor(anchorRef.current)}>
                                        <Select.Option value="" label={t("input:filter_form.not_set")}>
                                            {t("input:filter_form.not_set")}
                                        </Select.Option>
                                        {getFilteredExpenseStatusArrayForExport(exportFormat).map((status) => (
                                            <Select.Option key={status} value={`${status}`} label={t(expenseStati[status])}>
                                                {t(expenseStati[status])}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Collapse.Panel>
                )}
            </Collapse>

            <CommonFilterFields filterData={filterData} formInstance={formInstance} rules={rules} />
        </Form>
    )
}

export default ExportForm
